import React, { useCallback, useEffect, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
} from "react-native";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ParentStackParamList } from "../../navigation/types";
import {
  childrenApi,
  Child,
  ChildActivity,
  ActivityRange,
  Progress,
} from "../../api/children";
import { useAuthStore } from "../../state/authStore";
import { ParentTabBar , TAB_BAR_CLEARANCE } from "../../components/ParentTabBar";
import { VideoArt, paletteFor } from "../../components/VideoArt";
import { Sticker, ChevronRight } from "lucide-react-native";
import { awardsApi, AwardsResponse } from "../../api/awards";
import { StickerMedallion } from "../../components/StickerMedallion";
import { colors, fontFamily, radii } from "../../theme";

type Props = NativeStackScreenProps<ParentStackParamList, "Dashboard">;

const DAY_LETTERS = ["M", "T", "W", "T", "F", "S", "S"];

// Lowercase backend category names → display labels for the topic bars
// + recently-watched rows.
function titleCaseTopic(s: string): string {
  return s
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

const TOPIC_COLORS: Record<string, string> = {
  Numbers: colors.primary,
  Shapes: colors.success,
  Letters: colors.secondary,
  Colors: colors.pink,
  Social: colors.purple,
  Bedtime: "#3F51B5",
  Language: "#009688",
  Creativity: "#5BC0BE",
};

type DayData = { day: string; minutes: number };
type TopicData = { name: string; pct: number };
type RecentVideo = {
  id: string;
  title: string;
  minutes: number;
  stars: number;
};

export default function DashboardScreen({ navigation, route }: Props) {
  // childId may be omitted (Stats tab tapped from Library/Me). Resolve to the
  // first child below so the screen loads instead of bouncing through Home.
  // Sticker summary for the card below the TODAY stats. Best-effort: the card
  // degrades to its label-only form if this fails.
  const [awards, setAwards] = useState<AwardsResponse | null>(null);
  const [childId, setChildId] = useState<string | undefined>(
    route.params?.childId,
  );
  const { accessToken } = useAuthStore();
  const [child, setChild] = useState<Child | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  // Real activity payload from /v1/children/:id/activity. Drives the
  // weekly chart, favorite-topics bars, and recently-watched rows —
  // replacing the hardcoded mock arrays that used to live here.
  const [activity, setActivity] = useState<ChildActivity | null>(null);
  const [range, setRange] = useState<ActivityRange>("week");

  // No childId passed → default to the first child so the Stats tab works in
  // one tap from any screen.
  useEffect(() => {
    if (childId || !accessToken) return;
    childrenApi.list(accessToken).then((cs) => {
      if (cs?.[0]) setChildId(cs[0].id);
    });
  }, [childId, accessToken]);

  useEffect(() => {
    if (!accessToken || !childId) return;
    childrenApi.list(accessToken).then((children) => {
      const found = children?.find((c) => c.id === childId);
      if (found) setChild(found);
    });
    childrenApi
      .getProgress(childId, accessToken)
      .then(setProgress)
      .catch(() => {});
    awardsApi
      .list(childId, accessToken)
      .then(setAwards)
      .catch(() => setAwards(null));
  }, [childId, accessToken]);

  // Re-fetch activity whenever the parent flips the Day/Week/Month tab.
  // Best-effort: a network blip leaves `activity` as null and the chart
  // renders its empty state instead of crashing.
  useEffect(() => {
    if (!accessToken || !childId) return;
    let cancelled = false;
    childrenApi
      .getActivity(childId, accessToken, range)
      .then((a) => {
        if (!cancelled) setActivity(a);
      })
      .catch(() => {
        if (!cancelled) setActivity(null);
      });
    return () => {
      cancelled = true;
    };
  }, [childId, accessToken, range]);

  // Map activity → the per-day, per-topic, per-recent shapes the rest of
  // this screen renders. Falls back to empty arrays when not loaded yet.
  const weekData: DayData[] =
    activity?.weekly.map((w, i) => ({
      day: DAY_LETTERS[i] ?? "?",
      minutes: Math.round(w.secondsWatched / 60),
    })) ?? DAY_LETTERS.map((d) => ({ day: d, minutes: 0 }));
  const topics: TopicData[] =
    activity?.categories.slice(0, 5).map((c) => ({
      name: titleCaseTopic(c.name),
      pct: c.pct,
    })) ?? [];
  const recent: RecentVideo[] =
    activity?.recent.slice(0, 6).map((r, i) => ({
      id: `${r.at}-${i}`,
      title: r.videoTitle,
      // Real per-session figures straight from the API — minutes watched
      // and stars (correct answers). No fabricated accuracy proxy.
      minutes: r.minutes,
      stars: r.stars,
    })) ?? [];
  const hasSessions = (activity?.recent.length ?? 0) > 0;
  const periodWord =
    range === "day"
      ? "today"
      : range === "month"
        ? "in the last 30 days"
        : "this week";

  const todayMin = progress
    ? Math.round(progress.todayWatchedSeconds / 60)
    : 0;
  const dailyLimit = child?.controls?.dailyMinutes ?? 60;
  const accuracyRaw = progress?.accuracy ?? 0;
  const accuracy =
    accuracyRaw <= 1
      ? Math.round(accuracyRaw * 100)
      : Math.round(accuracyRaw);
  const correct = progress?.correctAnswers ?? 0;
  const total = progress?.totalQuestions ?? 0;
  const stars = correct;
  // Real current streak (consecutive days with a session) from the activity
  // payload. There's no "best streak ever" in the API, so we surface the
  // live streak instead of the old hardcoded "best: 14".
  const streakDays = activity?.streakDays ?? 0;

  const todayDow = (new Date().getDay() + 6) % 7; // 0..6, Mon-first

  const handleTab = (key: string) => {
    if (key === "home") navigation.navigate("ParentHome");
    if (key === "library") navigation.navigate("ParentLibrary");
    if (key === "controls") navigation.navigate("Controls", { childId });
    if (key === "me") navigation.navigate("Me");
  };

  return (
    <View style={styles.root}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.eyebrow}>
            {(child?.name ?? "...").toUpperCase()}'S WEEK
          </Text>
          <Text style={styles.headline}>Stats</Text>
        </View>

        {/* TODAY dark card */}
        <View style={styles.todayCard}>
          <Text style={styles.todayEyebrow}>TODAY</Text>
          <View style={styles.todayRow}>
            <DarkStat
              label="WATCH TIME"
              value={`${todayMin}`}
              sub={`of ${dailyLimit} min`}
              tone="white"
            />
            <DarkStat
              label="QUIZ"
              value={`${accuracy}%`}
              sub={`${correct} / ${total} right`}
              tone="green"
            />
            <DarkStat
              label="STARS"
              value={`${stars}`}
              sub={streakDays === 1 ? "1-day streak" : `${streakDays}-day streak`}
              tone="yellow"
            />
          </View>
        </View>

        {/* Sticker book — a real card rather than a link at the bottom of the
            page, where it was effectively invisible. Shows progress and a few
            earned badges so it reads as content worth tapping. */}
        <TouchableOpacity
          style={styles.stickerCard}
          onPress={() => navigation.navigate("StickerBook", { childId })}
          activeOpacity={0.88}
          accessibilityRole="button"
          accessibilityLabel="Open the sticker book"
        >
          <View style={styles.stickerCardTop}>
            <View style={styles.stickerIconWrap}>
              <Sticker size={19} color="#fff" strokeWidth={2.5} />
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.stickerCardTitle}>Sticker book</Text>
              <Text style={styles.stickerCardSub}>
                {awards
                  ? `${awards.earnedCount} of ${awards.totalCount} collected`
                  : "See what they've collected"}
              </Text>
            </View>
            <ChevronRight size={20} color={colors.primaryInk} strokeWidth={2.6} />
          </View>

          {/* Preview row: earned first, then locked, so there's always
              something to look at even on day one. */}
          {awards && awards.stickers.length > 0 && (
            <View style={styles.stickerPreview}>
              {[...awards.stickers]
                .sort((a, b) => Number(b.earned) - Number(a.earned))
                .slice(0, 6)
                .map((st) => (
                  <StickerMedallion
                    key={st.key}
                    stickerKey={st.key}
                    earned={st.earned}
                    size={38}
                  />
                ))}
            </View>
          )}
        </TouchableOpacity>

        {/* Day / Week / Month range tabs — drive the watch-time chart +
            categories + recently-watched list below via the activity
            fetch. Persisted in local state only (no localStorage on RN). */}
        <View style={styles.rangeRow}>
          {(["day", "week", "month"] as ActivityRange[]).map((r) => {
            const on = range === r;
            return (
              <TouchableOpacity
                key={r}
                style={[styles.rangeTab, on && styles.rangeTabOn]}
                onPress={() => setRange(r)}
                activeOpacity={0.85}
              >
                <Text
                  style={[
                    styles.rangeTabText,
                    on && styles.rangeTabTextOn,
                  ]}
                >
                  {r === "day" ? "Day" : r === "week" ? "Week" : "Month"}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Watch time chart */}
        <View style={styles.card}>
          <View style={styles.cardHeadRow}>
            <Text style={styles.cardTitle}>Watch time</Text>
            <Text style={styles.cardEyebrow}>
              {range === "day"
                ? "today"
                : range === "month"
                  ? "last 30 days"
                  : "this week"}
            </Text>
          </View>
          <View style={styles.chartArea}>
            {weekData.map((d, i) => {
              const isToday = i === todayDow;
              const h = Math.max(4, (d.minutes / 60) * 80);
              return (
                <View key={`${d.day}-${i}`} style={styles.chartCol}>
                  <View
                    style={[
                      styles.bar,
                      {
                        height: h,
                        backgroundColor: isToday
                          ? colors.primary
                          : colors.bgWarm,
                      },
                    ]}
                  />
                  <Text
                    style={[
                      styles.barLabel,
                      {
                        color: isToday ? colors.primary : colors.ink3,
                      },
                    ]}
                  >
                    {DAY_LETTERS[i]}
                  </Text>
                </View>
              );
            })}
          </View>
          {!hasSessions && (
            <Text style={styles.emptyNote}>No sessions yet {periodWord}.</Text>
          )}
        </View>

        {/* Favorite topics */}
        <View style={styles.card}>
          <Text style={[styles.cardTitle, { marginBottom: 10 }]}>
            Favorite topics
          </Text>
          {topics.map((t) => (
            <View key={t.name} style={styles.topicRow}>
              <View style={styles.topicHead}>
                <Text style={styles.topicName}>{t.name}</Text>
                <Text style={styles.topicPct}>{t.pct}%</Text>
              </View>
              <View style={styles.topicBarBg}>
                <View
                  style={[
                    styles.topicBarFill,
                    {
                      width: `${t.pct}%`,
                      backgroundColor: TOPIC_COLORS[t.name] ?? colors.primary,
                    },
                  ]}
                />
              </View>
            </View>
          ))}
          {topics.length === 0 && (
            <Text style={styles.emptyNote}>
              No topic data yet — topics appear after the first few sessions.
            </Text>
          )}
        </View>

        {/* Recently watched */}
        <Text style={styles.sectionLabel}>RECENTLY WATCHED</Text>
        <View style={{ gap: 8 }}>
          {recent.length === 0 ? (
            <Text style={styles.emptyNote}>
              Nothing yet — {child?.name ?? "your child"}'s viewing history
              shows up here after the first video.
            </Text>
          ) : (
            recent.map((r) => {
              const { palette, icon } = paletteFor(r.title);
              return (
                <View key={r.id} style={styles.recentRow}>
                  <View style={styles.recentThumb}>
                    <VideoArt
                      icon={icon}
                      palette={palette}
                      width={64}
                      height={40}
                    />
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={styles.recentTitle} numberOfLines={1}>
                      {r.title}
                    </Text>
                    <Text style={styles.recentCat}>
                      {r.minutes} min watched
                    </Text>
                  </View>
                  <Text style={[styles.recentAcc, { color: colors.star }]}>
                    {r.stars > 0 ? `⭐ ${r.stars}` : "—"}
                  </Text>
                </View>
              );
            })
          )}
        </View>

      </ScrollView>

      <ParentTabBar active="stats" onChange={handleTab} />
    </View>
  );
}

function DarkStat({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone: "white" | "green" | "yellow";
}) {
  const valueColor =
    tone === "green" ? "#7DF0A0" : tone === "yellow" ? colors.star : "#fff";
  return (
    <View style={darkStyles.box}>
      <Text style={darkStyles.label}>{label}</Text>
      <Text style={[darkStyles.value, { color: valueColor }]}>{value}</Text>
      <Text style={darkStyles.sub}>{sub}</Text>
    </View>
  );
}

const darkStyles = StyleSheet.create({
  box: {
    flex: 1,
    backgroundColor: "rgba(255,255,255,0.06)",
    borderRadius: 12,
    padding: 10,
  },
  label: {
    fontFamily,
    fontSize: 9,
    fontWeight: "800",
    color: "rgba(255,255,255,0.5)",
    letterSpacing: 1,
  },
  value: {
    fontFamily,
    fontSize: 24,
    fontWeight: "900",
    lineHeight: 26,
    marginTop: 2,
  },
  sub: {
    fontFamily,
    fontSize: 10,
    fontWeight: "700",
    color: "rgba(255,255,255,0.55)",
    marginTop: 3,
  },
});

const styles = StyleSheet.create({
  stickerCard: {
    backgroundColor: colors.bgWarm,
    borderWidth: 1.5,
    borderColor: colors.primary,
    borderRadius: radii.lg,
    padding: 16,
    marginBottom: 16,
  },
  stickerCardTop: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  stickerIconWrap: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: colors.primary,
    alignItems: "center",
    justifyContent: "center",
  },
  stickerCardTitle: {
    fontFamily,
    fontSize: 17,
    fontWeight: "900",
    color: colors.ink,
  },
  stickerCardSub: {
    fontFamily,
    fontSize: 13,
    fontWeight: "700",
    color: colors.primaryInk,
    marginTop: 1,
  },
  stickerPreview: {
    flexDirection: "row",
    gap: 8,
    marginTop: 14,
  },
  rangeRow: {
    flexDirection: "row",
    backgroundColor: colors.bgWarm,
    borderRadius: 999,
    padding: 4,
    marginBottom: 14,
    alignSelf: "flex-start",
  },
  rangeTab: {
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 999,
  },
  rangeTabOn: {
    backgroundColor: colors.surface,
  },
  rangeTabText: {
    fontFamily,
    fontSize: 13,
    fontWeight: "800",
    color: colors.ink3,
  },
  rangeTabTextOn: {
    color: colors.ink,
  },
  root: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  scroll: { flex: 1 },
  scrollContent: {
    paddingHorizontal: 16,
    paddingTop: 56,
    paddingBottom: TAB_BAR_CLEARANCE,
  },

  // Header
  header: {
    paddingHorizontal: 4,
    marginBottom: 16,
  },
  eyebrow: {
    fontFamily,
    fontSize: 11,
    fontWeight: "800",
    color: colors.ink2,
    letterSpacing: 1.4,
    marginBottom: 4,
  },
  headline: {
    fontFamily,
    fontSize: 30,
    fontWeight: "900",
    color: colors.ink,
    letterSpacing: -0.3,
  },

  // TODAY dark card
  todayCard: {
    backgroundColor: colors.ink,
    borderRadius: radii.lg,
    padding: 16,
    marginBottom: 12,
  },
  todayEyebrow: {
    fontFamily,
    fontSize: 11,
    fontWeight: "800",
    color: "rgba(255,255,255,0.55)",
    letterSpacing: 1.4,
  },
  todayRow: {
    flexDirection: "row",
    gap: 8,
    marginTop: 10,
  },

  // Generic card
  card: {
    backgroundColor: colors.surface,
    borderRadius: radii.lg,
    padding: 14,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: colors.line,
  },
  cardHeadRow: {
    flexDirection: "row",
    alignItems: "baseline",
    justifyContent: "space-between",
  },
  cardTitle: {
    fontFamily,
    fontSize: 14,
    fontWeight: "800",
    color: colors.ink,
  },
  cardEyebrow: {
    fontFamily,
    fontSize: 11,
    fontWeight: "800",
    color: colors.ink2,
    letterSpacing: 1.2,
  },
  emptyNote: {
    fontFamily,
    fontSize: 13,
    fontWeight: "600",
    color: colors.ink3,
    paddingVertical: 10,
    paddingHorizontal: 4,
  },

  // Bar chart
  chartArea: {
    flexDirection: "row",
    alignItems: "flex-end",
    height: 110,
    marginTop: 12,
  },
  chartCol: {
    flex: 1,
    alignItems: "center",
    gap: 6,
  },
  bar: {
    width: "70%",
    borderRadius: 6,
  },
  barLabel: {
    fontFamily,
    fontSize: 10,
    fontWeight: "800",
  },

  // Topics
  topicRow: {
    marginBottom: 10,
  },
  topicHead: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  topicName: {
    fontFamily,
    fontSize: 12,
    fontWeight: "800",
    color: colors.ink,
  },
  topicPct: {
    fontFamily,
    fontSize: 12,
    fontWeight: "800",
    color: colors.ink2,
  },
  topicBarBg: {
    height: 6,
    borderRadius: 4,
    backgroundColor: "rgba(0,0,0,0.06)",
    overflow: "hidden",
  },
  topicBarFill: {
    height: 6,
  },

  // Recently watched
  sectionLabel: {
    fontFamily,
    fontSize: 11,
    fontWeight: "800",
    color: colors.ink2,
    letterSpacing: 1.4,
    paddingHorizontal: 4,
    paddingBottom: 8,
  },
  recentRow: {
    backgroundColor: colors.surface,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.line,
    padding: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  recentThumb: {
    width: 64,
    height: 40,
    borderRadius: 8,
    overflow: "hidden",
  },
  recentTitle: {
    fontFamily,
    fontSize: 13,
    fontWeight: "800",
    color: colors.ink,
  },
  recentCat: {
    fontFamily,
    fontSize: 11,
    fontWeight: "700",
    color: colors.ink2,
  },
  recentAcc: {
    fontFamily,
    fontSize: 16,
    fontWeight: "900",
  },
  recentAccPct: {
    fontFamily,
    fontSize: 11,
    fontWeight: "800",
  },
});
